import { useCallback, useMemo, useState } from 'react';
import affiliateService from '@wlb/shared/api/affiliate/affiliateService';
import {
  createAffiliateEarningsFilter,
  isAffiliateEarningsRangeValid,
} from '@wlb/shared/api/affiliate/affiliateEarnings';
import type {
  Affiliate,
  AffiliateEarningsFilter,
} from '@wlb/shared/api/affiliate/affiliateTypes';
import { useAdminSession } from '@wlb/shared/context/AdminSessionContext';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { IconButton } from '../components/ui/IconButton';
import { Pagination } from '../components/ui/Pagination';
import { SelectField } from '../components/ui/SelectField';
import { StatusPill } from '../components/ui/StatusPill';
import { TextField } from '../components/ui/TextField';
import { PageHeader } from '../components/layout/PageHeader';
import { AffiliateEarningsFilters } from '../features/affiliates/AffiliateEarningsFilters';
import { AffiliatePayoutDialog } from '../features/affiliates/AffiliatePayoutDialog';
import { ReferralCodesDialog } from '../features/affiliates/ReferralCodesDialog';
import { useAsyncResource } from '../hooks/useAsyncResource';
import { formatCount, formatCurrency, formatDate } from '../utils/format';
import '../components/ui/ui.css';
import './pages.css';

const PAGE_SIZE = 20;

/** The API filters on the integer `isActive`, so the select carries its values. */
const STATUS_FILTERS = [
  { value: '', labelKey: 'affiliates.filters.all' },
  { value: '1', labelKey: 'affiliates.filters.approved' },
  { value: '0', labelKey: 'affiliates.filters.rejected' },
] as const;

export function Affiliates() {
  const { t } = useTranslation('admin');
  const { adminUser } = useAdminSession();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [codesFor, setCodesFor] = useState<Affiliate | null>(null);
  const [payoutFor, setPayoutFor] = useState<Affiliate | null>(null);
  const [earningsFilter, setEarningsFilter] = useState<AffiliateEarningsFilter>(() =>
    createAffiliateEarningsFilter(),
  );
  const debouncedSearch = useDebounce(search);
  const actionBy = adminUser?.userId ?? 0;

  const load = useCallback(
    () =>
      affiliateService.getAffiliates({
        searchKeyword: debouncedSearch.trim(),
        isActiveFilter: statusFilter === '' ? null : Number(statusFilter),
        pageNumber,
        pageSize: PAGE_SIZE,
        earningsFilter,
      }),
    [debouncedSearch, earningsFilter, pageNumber, statusFilter],
  );

  const requestKey = [
    debouncedSearch,
    statusFilter,
    pageNumber,
    earningsFilter.rangeId,
    earningsFilter.startDate,
    earningsFilter.endDate,
    earningsFilter.paymentState,
  ].join('|');

  // A half-typed or inverted custom range must not be sent: the service drops
  // unusable bounds, which would answer with all-time totals under a period
  // label. The rows already on screen stay while the field shows the error.
  const rangeUsable = isAffiliateEarningsRangeValid(earningsFilter);
  const { data, status, refreshing, error, reload } = useAsyncResource(
    load,
    requestKey,
    t('common.errorTitle'),
    rangeUsable,
    'affiliates',
  );

  const review = useCallback(
    async (affiliate: Affiliate, approve: boolean) => {
      setPendingId(affiliate.affiliateUserId);
      try {
        await affiliateService.reviewAffiliate(
          affiliate.affiliateUserId,
          approve,
          actionBy,
        );
        ToastService.show({
          type: 'success',
          message: approve
            ? t('affiliates.toasts.approved')
            : t('affiliates.toasts.rejected'),
        });
        reload();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      } finally {
        setPendingId(null);
      }
    },
    [actionBy, reload, t],
  );

  const columns = useMemo<readonly DataTableColumn<Affiliate>[]>(
    () => [
      {
        key: 'email',
        header: t('affiliates.columns.email'),
        render: row => (
          <>
            <span className="ui-table__primary">{row.email || t('common.none')}</span>
            {row.address ? (
              <span className="ui-table__secondary">{row.address}</span>
            ) : null}
          </>
        ),
      },
      {
        key: 'country',
        header: t('affiliates.columns.country'),
        render: row => row.country || t('common.none'),
      },
      {
        key: 'howDidYouHear',
        header: t('affiliates.columns.howDidYouHear'),
        render: row => (
          <span className="ui-table__truncate">
            {row.howDidYouHear || t('common.none')}
          </span>
        ),
      },
      {
        key: 'applied',
        header: t('affiliates.columns.applied'),
        render: row => formatDate(row.createdDate) || t('common.none'),
      },
      {
        key: 'reviewed',
        header: t('affiliates.columns.reviewed'),
        render: row => formatDate(row.reviewedDate) || t('common.none'),
      },
      {
        key: 'downloads',
        header: t('affiliates.columns.downloads'),
        numeric: true,
        render: row => formatCount(row.usageCount),
      },
      {
        key: 'totalEarnings',
        header: t('affiliates.columns.totalEarnings'),
        numeric: true,
        render: row => formatCurrency(row.totalEarnings),
      },
      {
        key: 'status',
        header: t('affiliates.columns.status'),
        render: row => (
          <StatusPill
            label={t(`status.${row.state}`)}
            tone={
              row.state === 'approved'
                ? 'success'
                : row.state === 'rejected'
                  ? 'danger'
                  : 'warning'
            }
          />
        ),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: row => (
          <div className="ui-table__actions">
            {row.state === 'approved' ? null : (
              <IconButton
                icon="check"
                tone="emerald"
                disabled={pendingId === row.affiliateUserId}
                label={t('affiliates.actions.approve')}
                onClick={() => review(row, true)}
              />
            )}
            {row.state === 'rejected' ? null : (
              <IconButton
                icon="close"
                tone="rose"
                disabled={pendingId === row.affiliateUserId}
                label={t('affiliates.actions.reject')}
                onClick={() => review(row, false)}
              />
            )}
            <IconButton
              icon="key"
              tone="amber"
              label={t('affiliates.actions.referralCodes')}
              onClick={() => setCodesFor(row)}
            />
            {/* Only an approved affiliate can be paid; the others have no
                agreement behind the balance yet. */}
            {row.state === 'approved' ? (
              <IconButton
                icon="wallet"
                tone="teal"
                label={t('affiliates.actions.payout')}
                onClick={() => setPayoutFor(row)}
              />
            ) : null}
          </div>
        ),
      },
    ],
    [pendingId, review, t],
  );

  const changeFilter = (apply: () => void) => {
    setPageNumber(1);
    apply();
  };

  return (
    <div className="page page--fill">
      <PageHeader
        title={t('affiliates.title')}
        subtitle={t('affiliates.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            // Reading is gated while the range is unusable, so an enabled
            // Refresh would be a button that silently does nothing.
            disabled={refreshing || !rangeUsable}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('common.search')}
            type="search"
            placeholder={t('affiliates.searchPlaceholder')}
            value={search}
            onChange={event => changeFilter(() => setSearch(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <SelectField
            label={t('affiliates.filters.statusLabel')}
            value={statusFilter}
            options={STATUS_FILTERS.map(option => ({
              value: option.value,
              label: t(option.labelKey),
            }))}
            onChange={event => changeFilter(() => setStatusFilter(event.target.value))}
          />
        </div>
      </section>

      <AffiliateEarningsFilters
        filter={earningsFilter}
        onFilterChange={next => {
          setPageNumber(1);
          setEarningsFilter(next);
        }}
        summary={data?.earnings ?? null}
      />

      <DataTable
        caption={t('affiliates.title')}
        columns={columns}
        rows={data?.items ?? []}
        rowKey={row => row.affiliateUserId}
        status={status}
        refreshing={refreshing}
        error={error}
        onRetry={rangeUsable ? reload : undefined}
        emptyTitle={t('affiliates.emptyTitle')}
        emptyMessage={t('affiliates.emptyMessage')}
        scrollResetKey={requestKey}
        footer={
          data ? (
            <Pagination
              pageNumber={data.pageNumber}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              hasPreviousPage={data.hasPreviousPage}
              hasNextPage={data.hasNextPage}
              pageSize={PAGE_SIZE}
              onChange={setPageNumber}
            />
          ) : null
        }
      />

      {codesFor ? (
        <ReferralCodesDialog
          affiliateUserId={codesFor.affiliateUserId}
          affiliateEmail={codesFor.email}
          onClose={() => setCodesFor(null)}
        />
      ) : null}

      {payoutFor ? (
        <AffiliatePayoutDialog
          affiliate={payoutFor}
          earningsFilter={earningsFilter}
          onClose={() => setPayoutFor(null)}
          onPaid={reload}
        />
      ) : null}
    </div>
  );
}
